import React, { useState, useEffect } from 'react';
import { FaTimes, FaEnvelope, FaCalendarAlt, FaComments, FaCheck, FaTrash, FaCog, FaFileAlt, FaTasks, FaExclamationTriangle } from 'react-icons/fa';
import FrontendNotificationsAPI from '../api/frontendNotifications';
import '../css/NotificationsModal.css';

const NotificationsModal = ({ open, onClose, onUnreadCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await FrontendNotificationsAPI.getNotifications({
        limit: 50,
        offset: 0
      });
      
      const notifications = data.notifications || [];
      const transformedNotifications = notifications.map(notif => ({
        id: notif.id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        timestamp: new Date(notif.timestamp),
        read: Boolean(notif.read),
        priority: notif.priority || 'medium'
      }));
      
      setNotifications(transformedNotifications);
      setUnreadCount(data.unreadCount || 0);
      onUnreadCountChange(data.unreadCount || 0);
    } catch (err) {
      console.warn('⚠️ Notifications not available:', err.message);
      setError('Hiba történt az értesítések betöltésekor.');
      setNotifications([]);
      setUnreadCount(0);
      onUnreadCountChange(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open]); 

  const markAsRead = async (id) => {
    try {
      // Update the backend
      await FrontendNotificationsAPI.markAsRead(id);
      
      // Reload notifications to get the updated read status from backend
      const data = await FrontendNotificationsAPI.getNotifications({
        limit: 50,
        offset: 0
      });
      
      const updatedNotifications = data.notifications || [];
      const updatedNotification = updatedNotifications.find(n => n.id === id);
      
      if (updatedNotification) {

        
        // Update the specific notification with the backend data
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === id ? { 
              ...notif, 
              read: Boolean(updatedNotification.read) 
            } : notif
          )
        );
        
        // Update unread count from backend data
        const newUnreadCount = updatedNotifications.filter(n => !n.read).length;
        setUnreadCount(newUnreadCount);
        onUnreadCountChange(newUnreadCount);
      }
      
    } catch (err) {
      console.error('❌ Error marking notification as read:', err);
      setError('Hiba történt az értesítés olvasottnak jelölésekor');
    }
  };

  const markAllAsRead = async () => {
    try {
      // Update the backend
      await FrontendNotificationsAPI.markAllAsRead();
      
      // Reload all notifications to get the updated read status from backend
      const data = await FrontendNotificationsAPI.getNotifications({
        limit: 50,
        offset: 0
      });
      
      const updatedNotifications = data.notifications || [];

      
      // Transform notifications with backend read status
      const transformedNotifications = updatedNotifications.map(notif => ({
        id: notif.id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        timestamp: new Date(notif.timestamp),
        read: Boolean(notif.read),
        priority: notif.priority || 'medium'
      }));
      
      // Update state with backend data
      setNotifications(transformedNotifications);
      
      // Update unread count from backend data
      const newUnreadCount = transformedNotifications.filter(n => !n.read).length;
      setUnreadCount(newUnreadCount);
      onUnreadCountChange(newUnreadCount);
      
    } catch (err) {
      console.error('❌ Error marking all notifications as read:', err);
      setError('Hiba történt az összes értesítés olvasottnak jelölésekor');
    }
  };

  const deleteNotification = async (id) => {
    try {
      await FrontendNotificationsAPI.deleteNotification(id);
      
      const notificationToDelete = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(notif => notif.id !== id));
      
      if (notificationToDelete && !notificationToDelete.read) {
        const remainingUnread = notifications.filter(n => !n.read && n.id !== id).length;
        setUnreadCount(remainingUnread);
        onUnreadCountChange(remainingUnread);
      }
    } catch (err) {
      console.error('❌ Error deleting notification:', err);
      setError('Hiba történt az értesítés törlésekor');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'calendar': return <FaCalendarAlt />;
      case 'mail': return <FaEnvelope />;
      case 'chat': return <FaComments />;
      case 'system': return <FaCog />;
      case 'document': return <FaFileAlt />;
      case 'task': return <FaTasks />;
      case 'alert': return <FaExclamationTriangle />;
      default: return <FaEnvelope />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#dc2626';
      case 'high': return '#e32f20';
      case 'medium': return '#ffa500';
      case 'low': return '#4caf50';
      default: return '#666';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Most';
    if (minutes < 60) return `${minutes} perce`;
    if (hours < 24) return `${hours} órával ezelőtt`;
    if (days < 7) return `${days} nappal ezelőtt`;
    return timestamp.toLocaleDateString('hu-HU');
  };

  const filteredNotifications = activeTab === 'all' 
    ? notifications 
    : notifications.filter(n => n.type === activeTab);

  if (!open) return null;

  return (
    <div className="notifications-modal-overlay" onClick={onClose}>
      <div className="notifications-modal" onClick={e => e.stopPropagation()}>
        <div className="notifications-modal-header">
          <h2>Értesítések</h2>
          <div className="notifications-modal-actions">
            <button 
              className="notifications-mark-all-read"
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
            >
              Összes olvasottnak jelölése
            </button>
            <button className="notifications-close" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="notifications-tabs">
          <button 
            className={`notifications-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            Összes ({notifications.length})
          </button>
          <button 
            className={`notifications-tab ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setActiveTab('calendar')}
          >
            Naptár ({notifications.filter(n => n.type === 'calendar').length})
          </button>
          <button 
            className={`notifications-tab ${activeTab === 'mail' ? 'active' : ''}`}
            onClick={() => setActiveTab('mail')}
          >
            Levelezés ({notifications.filter(n => n.type === 'mail').length})
          </button>
          <button 
            className={`notifications-tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            Chat ({notifications.filter(n => n.type === 'chat').length})
          </button>
          <button 
            className={`notifications-tab ${activeTab === 'system' ? 'active' : ''}`}
            onClick={() => setActiveTab('system')}
          >
            Rendszer ({notifications.filter(n => n.type === 'system').length})
          </button>
          <button 
            className={`notifications-tab ${activeTab === 'document' ? 'active' : ''}`}
            onClick={() => setActiveTab('document')}
          >
            Dokumentumok ({notifications.filter(n => n.type === 'document').length})
          </button>
          <button 
            className={`notifications-tab ${activeTab === 'task' ? 'active' : ''}`}
            onClick={() => setActiveTab('task')}
          >
            Feladatok ({notifications.filter(n => n.type === 'task').length})
          </button>
          <button 
            className={`notifications-tab ${activeTab === 'alert' ? 'active' : ''}`}
            onClick={() => setActiveTab('alert')}
          >
            Figyelmeztetések ({notifications.filter(n => n.type === 'alert').length})
          </button>
        </div>

        <div className="notifications-list">
          {error && (
            <div className="notifications-error">
              <p style={{color: 'red', padding: '20px', textAlign: 'center'}}>
                {error}
                <button 
                  onClick={() => {setError(null); loadNotifications();}}
                  style={{marginLeft: '10px', padding: '5px 10px', cursor: 'pointer'}}
                >
                  Újrapróbálás
                </button>
              </p>
            </div>
          )}
          
          {loading ? (
            <div className="notifications-loading">
              <p style={{padding: '40px', textAlign: 'center', color: 'var(--text-color-secondary)'}}>
                🔄 Értesítések betöltése...
              </p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="notifications-empty">
              <p>Nincsenek értesítések</p>
            </div>
          ) : (
            filteredNotifications.map(notification => (
              <div 
                key={notification.id} 
                className={`notification-item ${!notification.read ? 'unread' : ''}`}
              >
                <div className="notification-icon">
                  {getTypeIcon(notification.type)}
                  <div 
                    className="notification-priority"
                    style={{ backgroundColor: getPriorityColor(notification.priority) }}
                  ></div>
                </div>
                <div className="notification-content">
                  <div className="notification-header">
                    <h4 className="notification-title">{notification.title}</h4>
                    <span className="notification-time">
                      {formatTimestamp(notification.timestamp)}
                    </span>
                  </div>
                  <p className="notification-message">{notification.message}</p>
                </div>
                <div className="notification-actions">
                  {!notification.read && (
                    <button 
                      className="notification-action"
                      onClick={() => markAsRead(notification.id)}
                      title="Olvasottnak jelölés"
                    >
                      <FaCheck />
                    </button>
                  )}
                  <button 
                    className="notification-action"
                    onClick={() => deleteNotification(notification.id)}
                    title="Törlés"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsModal;
